<template>
  <div class="file-thumbnail-wrapper" :class="sizeClass">
    <!-- === IMAGE === -->
    <img v-if="fileType === 'image'"
         :src="resolvedImageSrc"
         :class="imgClass"
         :alt="altText" />

    <!-- === VIDEO: still processing === -->
    <div v-else-if="fileType === 'video' && isProcessing"
         class="processing-placeholder d-flex flex-column align-items-center justify-content-center">
      <b-spinner small></b-spinner>
      <small class="mt-2 text-muted">{{ processingLabel }}</small>
    </div>

    <!-- === VIDEO: processing failed === -->
    <div v-else-if="fileType === 'video' && processingStatus === 'Failed'"
         class="processing-failed d-flex flex-column align-items-center justify-content-center">
      <i class="fas fa-exclamation-triangle fa-2x text-danger"></i>
      <small class="mt-2 text-danger">Unable to process this video. Please re-upload or contact support.</small>
    </div>

    <!-- === VIDEO: ready (thumbnail or icon placeholder) === -->
    <div v-else-if="fileType === 'video'"
         class="video-thumb-container"
         @click="onVideoClick">
      <img v-if="thumbnailUrl && !thumbnailLoadError"
           :src="thumbnailUrl"
           :class="imgClass"
           :alt="altText"
           style="width:100%; height:100%; object-fit:cover;"
           @error="thumbnailLoadError = true" />
      <div v-else class="video-icon-placeholder d-flex align-items-center justify-content-center">
        <i class="fas fa-film fa-2x text-muted"></i>
      </div>
      <div class="play-overlay" v-if="showPlayIcon">
        <i class="fas fa-play-circle fa-3x text-white"></i>
      </div>
    </div>

    <!-- === DOCUMENT fallback === -->
    <div v-else class="doc-icon-placeholder d-flex align-items-center justify-content-center">
      <i class="fas fa-file fa-2x text-muted"></i>
    </div>
  </div>
</template>

<script>
/**
 * FileThumbnail.vue
 *
 * Reusable thumbnail display component for images and videos.
 * Handles thumbnail-or-fallback logic and video processing states.
 *
 * Events emitted:
 *   'play'  — (payload: url string) when user clicks a ready video thumbnail.
 *             url is processedVideoUrl if set, otherwise fullUrl.
 *             Parent should open VideoPlayerModal with this URL.
 */
export default {
  name: 'FileThumbnail',

  props: {
    /** thumbnailPublicUrl from API — 400px standard thumbnail */
    thumbnailUrl: { type: String, default: null },
    /** thumbnailMicroPublicUrl from API — 100px micro thumbnail */
    thumbnailMicroUrl: { type: String, default: null },
    /** publicUrl from API — full-resolution fallback */
    fullUrl: { type: String, default: null },
    /** alt text for accessibility */
    altText: { type: String, default: '' },
    /** 'image' | 'video' | 'document' */
    fileType: { type: String, default: 'image' },
    /** videoProcessingStatus from API — null | 'Pending' | 'Processing' | 'Complete' | 'Failed' */
    processingStatus: { type: String, default: null },
    /** processedVideoPublicUrl — transcoded MP4; null until Complete */
    processedVideoUrl: { type: String, default: null },
    /** CSS class(es) to apply to the img element */
    imgClass: { type: String, default: 'w-100' },
    /** whether to show a play-button overlay for ready videos */
    showPlayIcon: { type: Boolean, default: true },
    /** 'standard' = 400px thumbnail | 'micro' = 100px thumbnail */
    size: { type: String, default: 'standard' }
  },

  data () {
    return {
      thumbnailLoadError: false
    }
  },

  watch: {
    thumbnailUrl () {
      this.thumbnailLoadError = false
    }
  },

  computed: {
    sizeClass () {
      return this.size === 'micro' ? 'file-thumbnail-micro' : 'file-thumbnail-standard'
    },

    /**
     * Resolved image source for <img> elements.
     * size='micro'    → thumbnailMicroUrl → thumbnailUrl → fullUrl
     * size='standard' → thumbnailUrl → fullUrl
     */
    resolvedImageSrc () {
      if (this.size === 'micro') {
        return this.thumbnailMicroUrl || this.thumbnailUrl || this.fullUrl
      }
      return this.thumbnailUrl || this.fullUrl
    },

    isProcessing () {
      const s = this.processingStatus
      return s === 'Pending' || s === 'Processing'
    },

    processingLabel () {
      if (this.processingStatus === 'Pending') return 'Preparing your video…'
      if (this.processingStatus === 'Processing') return 'Getting your video ready…'
      return 'Processing video…'
    }
  },

  methods: {
    onVideoClick () {
      // Emit the best available playback URL
      const url = this.processedVideoUrl || this.fullUrl
      this.$emit('play', url)
    }
  }
}
</script>

<style scoped>
.file-thumbnail-wrapper {
  width: 100%;
}

.video-thumb-container {
  position: relative;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.6);
}

.processing-placeholder {
  min-height: 80px;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 8px;
}

.processing-failed {
  min-height: 80px;
  background: #fff0f0;
  border-radius: 4px;
  padding: 8px;
}

.video-icon-placeholder,
.doc-icon-placeholder {
  min-height: 80px;
  height: 100%;
  background: #f8f9fa;
  border-radius: 4px;
}

.file-thumbnail-micro .processing-placeholder,
.file-thumbnail-micro .video-icon-placeholder,
.file-thumbnail-micro .doc-icon-placeholder {
  min-height: 50px;
}
</style>
